import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  isMobile: boolean;
  isOpen: boolean;
  toggleSidebar: () => void;
  collapseSidebar: () => void;
  expandSidebar: () => void;
  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: ReactNode;
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();
  const toggleTimeoutRef = useRef<NodeJS.Timeout>();

  // Debounced resize handler for better performance
  const debouncedCheckMobile = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    
    resizeTimeoutRef.current = setTimeout(() => {
      const mobile = window.innerWidth < 768; // md breakpoint
      const wasWinMobile = isMobile;
      
      if (mobile !== wasWinMobile) {
        setIsMobile(mobile);

        // On mobile, force sidebar to be collapsed
        if (mobile) {
          setIsCollapsed(true);
        }
      }
    }, 150); // 150ms debounce
  }, [isMobile]);

  // Detect mobile breakpoint with performance optimization
  useEffect(() => {
    // Initial check
    const mobile = window.innerWidth < 768;
    setIsMobile(mobile);
    if (mobile) {
      setIsCollapsed(true);
    }

    window.addEventListener('resize', debouncedCheckMobile);
    return () => {
      window.removeEventListener('resize', debouncedCheckMobile);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (toggleTimeoutRef.current) {
        clearTimeout(toggleTimeoutRef.current);
      }
    };
  }, [debouncedCheckMobile]);

  // Debounced toggle for better touch responsiveness
  const toggleSidebar = useCallback(() => {
    if (toggleTimeoutRef.current) {
      clearTimeout(toggleTimeoutRef.current);
    }

    toggleTimeoutRef.current = setTimeout(() => {
      // Always use collapsed state, regardless of mobile or desktop
      setIsCollapsed(prev => !prev);
    }, 50); // Small debounce for touch responsiveness
  }, []);

  const collapseSidebar = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const expandSidebar = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const openMobileSidebar = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <SidebarContext.Provider value={{
      isCollapsed,
      isMobile,
      isOpen,
      toggleSidebar,
      collapseSidebar,
      expandSidebar,
      openMobileSidebar,
      closeMobileSidebar,
    }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
} 